import { ref } from 'vue';
import type { Ref } from 'vue';

import type { Email, Task } from '@stuut/api/models';
import { toast } from '@ui/components/sonner';

import { stuut } from '@/api';

export function useEmailResponseTasks(emailRef: Ref<Email>) {
  const fullResponseTasks = ref<Task[]>([]);
  const isLoadingTasks = ref(false);
  const isApplying = ref<Record<string, boolean>>({});

  const loadResponseTasks = async () => {
    const responseTasks = emailRef.value.openResponseTasks ?? [];
    if (!responseTasks.length || isLoadingTasks.value) return;
    isLoadingTasks.value = true;
    try {
      fullResponseTasks.value = await Promise.all(
        responseTasks.map((t) => stuut.tasks.retrieve(t.id)),
      );
    } catch {
      // best-effort fetch; surfacing the error would create noise on every hover/mount
    } finally {
      isLoadingTasks.value = false;
    }
  };

  const applyTaskAsDraft = async (
    task: Task,
    existingDraft?: Email | null,
  ): Promise<Email | null> => {
    if (isApplying.value[task.id]) return null;
    isApplying.value[task.id] = true;

    const responseData = task.responseData?.data;
    let draftEmail: Email | undefined;

    try {
      if (existingDraft) {
        if (responseData) {
          await stuut.emails.update(
            existingDraft.id,
            {
              data: {
                body: responseData.html_body,
                subject: responseData.subject,
                to: responseData.to,
                cc: responseData.cc,
              },
            },
            existingDraft,
          );
        }
        toast.success('Draft updated with response.');
        return existingDraft;
      }

      draftEmail = await stuut.emails.create({
        parent_email: emailRef.value.id,
        options: { reply_all: false },
      });

      if (responseData) {
        await stuut.emails.update(
          draftEmail.id,
          {
            data: {
              body: responseData.html_body,
              subject: responseData.subject,
              to: responseData.to,
              cc: responseData.cc,
            },
          },
          draftEmail,
        );
      }

      toast.success('Draft reply created.');
      return draftEmail;
    } catch (err: any) {
      if (draftEmail && !existingDraft) {
        try {
          await stuut.emails.delete(draftEmail.id, { data: {} });
        } catch {
          // best-effort cleanup
        }
      }
      toast.error(err.clientMessage || 'Failed to apply task as reply.');
      return null;
    } finally {
      isApplying.value[task.id] = false;
    }
  };

  return {
    applyTaskAsDraft,
    fullResponseTasks,
    isApplying,
    isLoadingTasks,
    loadResponseTasks,
  };
}
